import { useCallback, useEffect, useState, useMemo } from 'react';

import { useLazyMount } from '@page/core/hooks/utils/useLazyMount';
import { cn } from '../../lib/styles';
import { Portal } from '../../components/Portal';
import { DialogLayout, DialogClose } from '../DialogLayout';
import { DialogContext } from './DialogContext';
import { DialogTrigger } from './DialogTrigger';
import { DialogOverlay } from './DialogOverlay';
import { DialogContainer } from './DialogContainer';

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  closeOnInteractOutside?: boolean;
  onOpenChange?: (open: boolean) => void;

  trigger?: React.ReactNode;
  children?: React.ReactNode;

  /** renders `DialogLayout` as child */
  asChild?: boolean;

  className?: string;
  dialogClassName?: string;
  overlayClassName?: string;
  layoutClassName?: string;
}

const ANIMATION_DURATION = 100;

export function Dialog({
  open: controlledOpen,
  defaultOpen = false,
  closeOnInteractOutside = true,
  onOpenChange,

  trigger,
  children,

  asChild,

  className,
  dialogClassName,
  overlayClassName,
  layoutClassName,
}: DialogProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const isVisible = useLazyMount(isOpen, ANIMATION_DURATION);
  const animationState = isOpen ? 'open' : 'closed';

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const openDialog = useCallback(() => {
    handleOpenChange(true);
  }, [handleOpenChange]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnInteractOutside) {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnInteractOutside, close]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnInteractOutside && event.target === event.currentTarget) {
        close();
      }
    },
    [close, closeOnInteractOutside],
  );

  const contextValue = useMemo(() => ({ close }), [close]);

  const triggerElement = trigger ? (
    <DialogTrigger asChild onClick={openDialog}>
      {trigger}
    </DialogTrigger>
  ) : null;

  return (
    <DialogContext.Provider value={contextValue}>
      {triggerElement}

      <Portal>
        <dialog
          data-slot="dialog"
          data-state={animationState}
          className={cn(
            'p-0 m-0 border-none bg-transparent max-w-none max-h-none w-screen h-screen backdrop:hidden z-400 pointer-events-auto [&_*:pointer-events-auto]',
            dialogClassName,
          )}
          open={isVisible}
          onClick={(event) => event.stopPropagation()}
        >
          <DialogOverlay state={animationState} className={overlayClassName} onClick={handleOverlayClick} />

          <DialogContainer state={animationState} className={className}>
            <DialogLayout className={layoutClassName} asChild={asChild}>
              {children}
            </DialogLayout>
          </DialogContainer>
        </dialog>
      </Portal>
    </DialogContext.Provider>
  );
}

export { DialogClose, DialogTrigger };
